/**
 * PrintModal — Impression plan de table / liste / marque-places
 * Dialog Shadcn avec génération PDF (html2canvas + jsPDF) et window.print()
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Loader2, Printer, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  ToggleGroup, ToggleGroupItem,
} from '../../components/ui/toggle-group';
import { formatDate } from '../../utils/dateUtils';

// ─── Constantes ───────────────────────────────────────────────────────────────

const ORNAMENT = '❦ ❦ ❦';

// Dimensions px des pages (96 dpi, utilisées pour le rendu)
const PX = {
  A4: { landscape: { w: 1123, h: 794 },  portrait: { w: 794,  h: 1123 } },
  A3: { landscape: { w: 1587, h: 1123 }, portrait: { w: 1123, h: 1587 } },
};
// Dimensions mm pour jsPDF
const MM = {
  A4: { landscape: [297, 210], portrait: [210, 297] },
  A3: { landscape: [420, 297], portrait: [297, 420] },
};

function slugify(s) {
  return (s || 'plan').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const DIET_SHORT = {
  vegetarian: 'Végé', vegan: 'Vegan', halal: 'Halal', kid: 'Enfant', other: '?',
};

// ─── Helpers HTML ─────────────────────────────────────────────────────────────

function buildPageStyle(size, orient) {
  const d = PX[size][orient];
  return `
    width: ${d.w}px; height: ${d.h}px; overflow: hidden;
    background: #FFFFFF; box-sizing: border-box;
    page-break-after: always; break-after: page;
    font-family: 'Montserrat', sans-serif;
    position: relative;
  `;
}

function pageHeader(plan, subtitle) {
  const date = plan?.date_evenement
    ? formatDate(plan.date_evenement)
    : '';
  return `
    <div style="text-align:center; padding: 28px 36px 18px; border-bottom: 2.5px solid #D4AF37;">
      <h1 style="font-family:'Playfair Display',serif; font-size:22px; font-weight:700;
        color:#D4AF37; margin:0 0 4px; letter-spacing:0.02em;">
        ${plan?.nom_evenement || 'Événement'}
      </h1>
      <p style="font-size:11px; color:#9CA3AF; margin:0;">
        ${subtitle}${date ? ' · ' + date : ''}${plan?.lieu ? ' · ' + plan.lieu : ''}
      </p>
    </div>
  `;
}

function pageFooter() {
  return `
    <div style="position:absolute; bottom:12px; left:0; right:0; text-align:center;
      font-size:9px; color:#C4B5A0; letter-spacing:0.06em;">
      Plan de Table · ${formatDate(new Date())}
    </div>
  `;
}

// ─── Composant PrintSheet (rendu React pour html2canvas) ─────────────────────

function PrintPage({ size, orient, children }) {
  const d = PX[size][orient];
  return (
    <div style={{
      width: d.w, height: d.h, overflow: 'hidden',
      background: '#FFFFFF', boxSizing: 'border-box',
      fontFamily: "'Montserrat', sans-serif",
      position: 'relative', flexShrink: 0,
    }}>
      {children}
    </div>
  );
}

// ─── MODAL PRINCIPAL ──────────────────────────────────────────────────────────

export default function PrintModal({ open, onClose, plan, tables = [], invites = [], groups = [] }) {
  const [size,        setSize]        = useState('A4');
  const [orient,      setOrient]      = useState('landscape');
  const [content,     setContent]     = useState('plan-list');
  const [perPage,     setPerPage]     = useState('8');
  const [pdfLoading,  setPdfLoading]  = useState(false);
  const [showSheet,   setShowSheet]   = useState(false);
  const [pdfTrigger,  setPdfTrigger]  = useState(false);  // true = do PDF after render
  const printSheetRef = useRef(null);

  // groupMap
  const groupMap = {};
  groups.forEach(g => { groupMap[g.id] = g; });

  // invités placés
  const placed = invites.filter(i => i.table_id != null);
  const tableMap = {};
  tables.forEach(t => { tableMap[t.id] = t; });

  // Validation
  function validate() {
    if (content !== 'escort' && tables.filter(t => (t.capacity || 0) > 0).length === 0) {
      toast.error('Ajoutez au moins une table avant d\'imprimer');
      return false;
    }
    if (content === 'escort' && placed.length === 0) {
      toast.error('Aucun invité placé — assignez des invités à des tables d\'abord');
      return false;
    }
    return true;
  }

  // ── Générer les pages React ────────────────────────────────────────────────
  function buildPages() {
    const pages = [];
    const d = PX[size][orient];
    const padV = 20, padH = 36;
    const bodyH = d.h - 110 - 30; // header + footer

    if (content === 'plan' || content === 'plan-list') {
      // Page 1 — Plan visuel
      pages.push(
        <PrintPage key="plan" size={size} orient={orient}>
          {/* Header */}
          <div style={{ textAlign:'center', padding:'20px 36px 14px', borderBottom:'2.5px solid #D4AF37' }}>
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700,
              color:'#D4AF37', margin:'0 0 3px' }}>
              {plan?.nom_evenement || 'Événement'}
            </h1>
            <p style={{ fontSize:10, color:'#9CA3AF', margin:0 }}>
              Plan de Table
              {plan?.date_evenement && ` · ${formatDate(plan.date_evenement)}`}
              {plan?.lieu && ` · ${plan.lieu}`}
            </p>
          </div>
          {/* Capture canvas */}
          <div id="plan-capture-zone" style={{ position:'relative', overflow:'hidden',
            height: bodyH, display:'flex', alignItems:'center', justifyContent:'center',
            padding: `${padV}px ${padH}px` }}>
            {/* Canvas sera inséré ici par buildPlanCapture() */}
          </div>
          {/* Footer */}
          <div style={{ position:'absolute', bottom:10, left:0, right:0, textAlign:'center',
            fontSize:9, color:'#C4B5A0', letterSpacing:'0.06em' }}>
            Plan de Table · {formatDate(new Date())}
          </div>
        </PrintPage>
      );
    }

    if (content === 'plan-list') {
      // Page 2 — Liste par table
      const cols = orient === 'landscape' ? 2 : 1;
      const tableSitting = tables
        .filter(t => (t.capacity || 0) > 0)
        .map(t => ({
          ...t,
          guests: invites.filter(i => i.table_id === t.id),
        }));

      pages.push(
        <PrintPage key="list" size={size} orient={orient}>
          <div style={{ textAlign:'center', padding:'20px 36px 14px', borderBottom:'2.5px solid #D4AF37' }}>
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700,
              color:'#D4AF37', margin:'0 0 3px' }}>
              {plan?.nom_evenement || 'Événement'}
            </h1>
            <p style={{ fontSize:10, color:'#9CA3AF', margin:0 }}>Liste des invités par table</p>
          </div>
          <div style={{
            padding: '14px 32px',
            columnCount: cols,
            columnGap: 24,
            height: bodyH + 20, overflow: 'hidden',
          }}>
            {tableSitting.map(t => (
              <div key={t.id} style={{ breakInside:'avoid', marginBottom:12,
                padding:'8px 10px', border:'1px solid #F3F4F6', borderRadius:6 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:13, color:'#1F2937' }}>
                    {t.label}
                  </span>
                  <span style={{ fontSize:9, color:'#9CA3AF' }}>
                    {t.guests.length}/{t.capacity}
                  </span>
                </div>
                <div style={{ fontSize:11, lineHeight:1.7, color:'#374151' }}>
                  {t.guests.map((g, i) => {
                    const grp = groupMap[g.group_id];
                    const diet = g.diet && g.diet !== 'standard' ? ` (${DIET_SHORT[g.diet] || g.diet})` : '';
                    return (
                      <span key={g.id}>
                        {i > 0 && <span style={{ color:'#C4B5A0', margin:'0 3px' }}>•</span>}
                        <span style={{ color: grp?.color || '#374151', fontWeight:500 }}>
                          {g.nom}
                        </span>
                        {diet && <span style={{ fontStyle:'italic', color:'#9CA3AF', fontSize:10 }}>{diet}</span>}
                      </span>
                    );
                  })}
                  {t.guests.length === 0 && (
                    <span style={{ color:'#D1D5DB', fontStyle:'italic', fontSize:10 }}>Table vide</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div style={{ position:'absolute', bottom:10, left:0, right:0, textAlign:'center',
            fontSize:9, color:'#C4B5A0', letterSpacing:'0.06em' }}>
            Plan de Table · {formatDate(new Date())}
          </div>
        </PrintPage>
      );
    }

    if (content === 'escort') {
      const pp = parseInt(perPage, 10) || 8;
      const cols = pp === 4 ? 2 : pp === 8 ? 2 : 3;
      const rows = pp / cols;
      const pageCount = Math.ceil(placed.length / pp);

      for (let p = 0; p < pageCount; p++) {
        const slice = placed.slice(p * pp, (p + 1) * pp);
        // Remplir avec cards vides
        while (slice.length < pp) slice.push(null);

        const d = PX[size][orient];
        const cardW = Math.floor((d.w - 32 - (cols - 1) * 8) / cols);
        const cardH = Math.floor((d.h - 32 - (rows - 1) * 8) / rows);

        pages.push(
          <PrintPage key={`escort-${p}`} size={size} orient={orient}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gridTemplateRows: `repeat(${rows}, 1fr)`,
              gap: 8, padding: 16,
              height: '100%', boxSizing: 'border-box',
            }}>
              {slice.map((g, i) => {
                const tbl = g ? tableMap[g.table_id] : null;
                if (!g) {
                  return (
                    <div key={i} style={{
                      border: '1px dotted #E5E7EB', borderRadius: 4,
                      opacity: 0.25,
                    }} />
                  );
                }
                return (
                  <div key={g.id} style={{
                    border: '1px dashed #D1D5DB', borderRadius: 4,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    padding: 10, textAlign: 'center', gap: 4,
                    breakInside: 'avoid',
                  }}>
                    <span style={{ fontSize: 12, color: '#D4AF37', opacity: 0.55, letterSpacing: '0.15em' }}>
                      {ORNAMENT}
                    </span>
                    <span style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: Math.min(16, cardH / 6),
                      fontWeight: 700, color: '#1F2937', lineHeight: 1.2,
                    }}>
                      {g.nom}
                    </span>
                    <span style={{ fontSize: 11, fontStyle: 'italic', color: '#9CA3AF' }}>
                      {tbl?.label || '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </PrintPage>
        );
      }
    }

    return pages;
  }

  // ── Insérer la capture du canvas dans la page Plan ─────────────────────────
  async function insertPlanCapture(pageEl, html2canvas) {
    const canvasEl = document.getElementById('pdt-canvas-inner');
    const zone = pageEl.querySelector('#plan-capture-zone');
    if (!canvasEl || !zone) return;

    const d = PX[size][orient];
    const bodyH = d.h - 110 - 30;
    const bodyW = d.w - 72;

    const capture = await html2canvas(canvasEl, {
      scale: 0.5, backgroundColor: '#FAF6EE', useCORS: true, logging: false,
    });

    const scaleX = bodyW / capture.width;
    const scaleY = bodyH / capture.height;
    const scale  = Math.min(scaleX, scaleY, 1) * 0.92;
    const finalW = capture.width * scale;
    const finalH = capture.height * scale;

    // Retirer les boutons flottants du canvas (ils sont outside le canvas, donc OK)
    const img = document.createElement('img');
    img.src    = capture.toDataURL('image/jpeg', 0.9);
    img.style.cssText = `width:${finalW}px; height:${finalH}px; display:block; border-radius:4px;`;
    zone.style.display = 'flex';
    zone.style.alignItems = 'center';
    zone.style.justifyContent = 'center';
    zone.innerHTML = '';
    zone.appendChild(img);
  }

  // ── Déclencher impression/PDF ──────────────────────────────────────────────
  const handlePrint = useCallback(async () => {
    if (!validate()) return;
    setShowSheet(true);
    setPdfTrigger(false);
    await new Promise(r => setTimeout(r, 150));
    // Injecter @page CSS et appeler window.print()
    let styleEl = document.getElementById('pdt-print-style');
    if (!styleEl) { styleEl = document.createElement('style'); styleEl.id = 'pdt-print-style'; document.head.appendChild(styleEl); }
    const d = PX[size][orient];
    const pgW = size === 'A4' ? (orient === 'landscape' ? '297mm 210mm' : '210mm 297mm') : (orient === 'landscape' ? '420mm 297mm' : '297mm 420mm');
    styleEl.textContent = `@page { size: ${pgW}; margin: 0; } @media print { body > * { display:none!important; } #pdt-print-container { display:block!important; } }`;
    window.print();
    setShowSheet(false);
    styleEl.textContent = '';
  }, [size, orient, content, perPage, validate]); // eslint-disable-line

  const handlePDF = useCallback(async () => {
    if (!validate()) return;
    setPdfLoading(true);
    setShowSheet(true);
    setPdfTrigger(true);
  }, [size, orient, content, perPage, validate]); // eslint-disable-line

  // Quand le sheet est rendu et pdfTrigger est true → générer le PDF
  useEffect(() => {
    if (!showSheet || !pdfTrigger || !printSheetRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
          import('jspdf'),
          import('html2canvas'),
        ]);

        // Si mode plan : insérer la capture
        if (content !== 'escort') {
          const pages = printSheetRef.current.querySelectorAll('.print-page-wrapper');
          const planPage = pages[0];
          if (planPage) await insertPlanCapture(planPage, html2canvas);
        }

        await new Promise(r => setTimeout(r, 100));
        if (cancelled) return;

        const [pw, ph] = MM[size][orient];
        const pdf = new jsPDF({ orientation: orient === 'landscape' ? 'l' : 'p', unit: 'mm', format: size.toLowerCase() });

        const pageEls = printSheetRef.current.querySelectorAll('.print-page-wrapper');
        for (let i = 0; i < pageEls.length; i++) {
          if (cancelled) break;
          const canvas = await html2canvas(pageEls[i], {
            scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false,
          });
          const imgData = canvas.toDataURL('image/jpeg', 0.92);
          if (i > 0) pdf.addPage(size.toLowerCase(), orient === 'landscape' ? 'l' : 'p');
          pdf.addImage(imgData, 'JPEG', 0, 0, pw, ph);
        }

        const tag = content === 'escort' ? 'marque-places' : 'plan-table';
        pdf.save(`${tag}-${slugify(plan?.nom_evenement)}-${size}.pdf`);
        toast.success(`✓ ${pageEls.length} page${pageEls.length > 1 ? 's' : ''} téléchargée${pageEls.length > 1 ? 's' : ''}`);
      } catch (e) {
        if (!cancelled) toast.error('Erreur PDF : ' + e.message);
      } finally {
        if (!cancelled) { setPdfLoading(false); setShowSheet(false); setPdfTrigger(false); }
      }
    })();

    return () => { cancelled = true; };
  }, [showSheet, pdfTrigger]); // eslint-disable-line

  const pages = showSheet ? buildPages() : [];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog open={open} onOpenChange={v => { if (!v && !pdfLoading) onClose(); }}>
        <DialogContent style={{ maxWidth: 480, fontFamily:"'Montserrat',sans-serif" }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700 }}>
              Imprimer ou exporter en PDF
            </DialogTitle>
            <p style={{ fontSize:11, color:'#9CA3AF', margin:'2px 0 0' }}>
              Choisis le format, le contenu et les options
            </p>
          </DialogHeader>

          <div style={{ display:'flex', flexDirection:'column', gap:16, padding:'4px 0' }}>

            {/* Format papier */}
            <Section label="Format papier">
              <ToggleGroup type="single" value={size} onValueChange={v => v && setSize(v)}
                style={{ display:'flex', gap:6 }}>
                <TGI value="A4">A4 — 21×29.7cm</TGI>
                <TGI value="A3">A3 — 29.7×42cm</TGI>
              </ToggleGroup>
            </Section>

            {/* Orientation */}
            <Section label="Orientation">
              <ToggleGroup type="single" value={orient} onValueChange={v => v && setOrient(v)}
                style={{ display:'flex', gap:6 }}>
                <TGI value="landscape">Paysage</TGI>
                <TGI value="portrait">Portrait</TGI>
              </ToggleGroup>
            </Section>

            {/* Contenu */}
            <Section label="Contenu">
              <ToggleGroup type="single" value={content} onValueChange={v => v && setContent(v)}
                style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <TGI value="plan-list">Plan + liste</TGI>
                <TGI value="plan">Plan seul</TGI>
                <TGI value="escort">Marque-places</TGI>
              </ToggleGroup>
            </Section>

            {/* Per page (marque-places seulement) */}
            {content === 'escort' && (
              <Section label="Marque-places par feuille">
                <ToggleGroup type="single" value={perPage} onValueChange={v => v && setPerPage(v)}
                  style={{ display:'flex', gap:6 }}>
                  <TGI value="4">4 (2×2)</TGI>
                  <TGI value="8">8 (2×4)</TGI>
                  <TGI value="12">12 (3×4)</TGI>
                </ToggleGroup>
              </Section>
            )}

            {/* Info */}
            <div style={{ padding:'10px 12px', borderRadius:8,
              background:'rgba(212,175,55,.05)', border:'1px solid rgba(212,175,55,.2)' }}>
              <p style={{ fontSize:11, color:'#9CA3AF', margin:0, lineHeight:1.5 }}>
                💡 <strong>PDF</strong> télécharge directement.
                <strong> Imprimer</strong> ouvre le dialogue du navigateur
                (qui propose aussi "Enregistrer en PDF").
              </p>
            </div>
          </div>

          <DialogFooter style={{ gap:8, marginTop:4 }}>
            <button onClick={onClose} disabled={pdfLoading}
              style={ghostBtn}>
              Annuler
            </button>
            <button onClick={handlePrint} disabled={pdfLoading}
              style={{ ...outlineBtn, display:'flex', alignItems:'center', gap:5 }}>
              <Printer size={13} /> Imprimer
            </button>
            <button onClick={handlePDF} disabled={pdfLoading}
              style={{ ...goldBtn, display:'flex', alignItems:'center', gap:5,
                opacity: pdfLoading ? 0.7 : 1, cursor: pdfLoading ? 'not-allowed' : 'pointer' }}>
              {pdfLoading
                ? <><Loader2 size={13} className="animate-spin" /> Génération…</>
                : <><FileDown size={13} /> PDF</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print sheet (off-screen) */}
      {showSheet && (
        <div
          ref={printSheetRef}
          id="pdt-print-container"
          style={{
            position: 'fixed', left: '-9999px', top: 0,
            zIndex: -1, pointerEvents: 'none',
            display: 'flex', flexDirection: 'column', gap: 0,
          }}
        >
          {pages.map((page, i) => (
            <div key={i} className="print-page-wrapper" style={{ display:'block' }}>
              {page}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({ label, children }) {
  return (
    <div>
      <p style={{ fontSize:10, fontWeight:700, textTransform:'uppercase',
        letterSpacing:'0.08em', color:'#9CA3AF', margin:'0 0 7px' }}>
        {label}
      </p>
      {children}
    </div>
  );
}

function TGI({ value, children }) {
  return (
    <ToggleGroupItem value={value}
      style={{ fontSize:12, fontWeight:600, padding:'6px 12px', borderRadius:7,
        fontFamily:"'Montserrat',sans-serif", flex: 1 }}>
      {children}
    </ToggleGroupItem>
  );
}

const ghostBtn   = { padding:'8px 16px', borderRadius:8, border:'none', background:'#F3F4F6',
  color:'#6B7280', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:"'Montserrat',sans-serif" };
const outlineBtn = { padding:'8px 14px', borderRadius:8, border:'1.5px solid #D1D5DB',
  background:'transparent', color:'#374151', fontSize:12, fontWeight:600, cursor:'pointer',
  fontFamily:"'Montserrat',sans-serif" };
const goldBtn    = { padding:'8px 16px', borderRadius:8, border:'none', background:'#D4AF37',
  color:'#0B1E20', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Montserrat',sans-serif" };
